import { useLayoutEffect, useRef, useState } from 'react';

// A dependency-free line/area chart for one or more series over a categorical x
// axis (months, days), with a numeric y axis anchored at zero.
//
// Every series shares the one y axis, which is only honest while they share a
// unit: several services' spend in USD, yes; USD beside SGD, never — two scales on
// one plot invent a comparison that is not in the data. The caller renders one
// chart per unit (see chart-multiples) and puts the comparable series inside it.
//
// It is drawn at the container's *measured* pixel width rather than scaled from a
// fixed viewBox. A scaled viewBox takes the axis text and the stroke widths with
// it, so the same chart would render hairline 11px type in a narrow layout and
// heavy 21px type on a wide monitor. Measuring costs one ResizeObserver and keeps
// every spec — 2px lines, 8px markers, 11px labels — true at any width.
//
// props:
//   labels       x categories, oldest first. This is the axis: a label with no
//                value is drawn as a gap, not as a line straight across it.
//   series       [{ key, label, values, color }] — values align 1:1 with labels,
//                and a null/undefined entry means "nothing reported here". color is
//                optional; without it the series takes a slot from the palette.
//   formatValue  value → string, for the tooltip and the end label
//   formatTick   value → string for the y ticks (defaults to formatValue)
//   formatLabel  (label, index) → short string for the x ticks
//   formatPoint  (label, index) → the tooltip's heading (defaults to formatLabel,
//                but an axis abbreviated to "Aug" wants "August 2026" here)
//   height       chart height in px
//   ariaLabel    what the chart shows, for a screen reader reaching the SVG

// The categorical slots, in assignment order. Validated against both theme
// surfaces (#ffffff and #131d33) for the lightness band, the chroma floor, 3:1
// contrast, and separation between *every* pair — not just neighbours in this list
// — under protanopia, deuteranopia, and tritanopia, because slots are handed out
// by key rather than in order.
//
// Four is the ceiling, deliberately. A fifth generated hue is indistinguishable
// from one of these to a reader with a colour-vision deficiency, so a longer list
// folds its tail into one "Other" series (the caller's job) instead.
const SLOTS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)'];

// A slot per series *key*, never per position: filtering one series out, or a
// service overtaking another in the sort, must not repaint the survivors — a reader
// who learned "Compute is indigo" would otherwise be misled by the same chart a
// moment later. The key hashes to its preferred slot and a collision takes the next
// free one; series that name their own colour (an "Other" fold, an emphasis grey)
// are left out of the draw entirely.
const slotsFor = (series) => {
  const taken = new Array(SLOTS.length).fill(false);
  const out = new Map();
  for (const s of series) {
    if (s.color) {
      out.set(s.key, s.color);
      continue;
    }
    const key = String(s.key);
    let slot = 0;
    for (let i = 0; i < key.length; i += 1) slot = (slot + key.charCodeAt(i)) % SLOTS.length;
    while (taken[slot]) slot = (slot + 1) % SLOTS.length;
    taken[slot] = true;
    out.set(s.key, SLOTS[slot]);
  }
  return out;
};

const TICKS = 4;
const PAD = { top: 18, right: 20, bottom: 28, left: 66 };

// The y axis tops out at a round number so the ticks read 0 / 500 / 1,000 rather
// than 0 / 437 / 874. An all-zero (or absent) series still gets a 0–1 axis, so the
// chart draws its baseline instead of collapsing to a line.
const niceMax = (max, ticks) => {
  if (!(max > 0)) return 1;
  const rough = max / ticks;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const norm = rough / magnitude;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * magnitude;
  return step * ticks;
};

// The consecutive runs of indices that carry a value. Each run is drawn as its own
// path, which is what leaves a month the provider never reported as a break in the
// line: joining across it would assert a spend nobody reported.
const runsOf = (values) => {
  const runs = [];
  let run = null;
  values.forEach((v, i) => {
    if (v == null || Number.isNaN(v)) {
      run = null;
      return;
    }
    if (!run) {
      run = [];
      runs.push(run);
    }
    run.push(i);
  });
  return runs;
};

// Measures the element the chart draws into, so the SVG can be 1:1 with pixels.
const useMeasuredWidth = (fallback) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // The floor keeps the axis from folding in on itself while a panel animates,
    // or when a not-yet-visible card measures zero.
    const measure = () => setWidth(Math.max(Math.round(el.clientWidth), 320));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
};

export default function LineChart({
  labels = [],
  series = [],
  formatValue = (v) => String(v),
  formatTick,
  formatLabel = (label) => label,
  formatPoint,
  height = 240,
  ariaLabel = 'Line chart',
  emptyText = 'No data',
}) {
  const [box, width] = useMeasuredWidth(720);
  // Which x the crosshair and tooltip are reading; null when the pointer is away
  // and the chart is its static self.
  const [active, setActive] = useState(null);

  const count = labels.length;
  const tick = formatTick || formatValue;
  const point = formatPoint || formatLabel;
  const plotW = Math.max(width - PAD.left - PAD.right, 10);
  const plotH = Math.max(height - PAD.top - PAD.bottom, 10);

  const top = niceMax(
    Math.max(
      0,
      ...series.flatMap((s) => (s.values || []).filter((v) => v != null && !Number.isNaN(v)))
    ),
    TICKS
  );
  // A single point has no line to draw, so it sits in the middle of the plot
  // rather than pinned to its left edge.
  const x = (i) => (count > 1 ? PAD.left + (plotW * i) / (count - 1) : PAD.left + plotW / 2);
  const y = (v) => PAD.top + plotH * (1 - v / top);
  const band = count > 1 ? plotW / (count - 1) : plotW;

  if (count === 0 || series.length === 0) {
    return <div className="chart chart--empty">{emptyText}</div>;
  }

  const colors = slotsFor(series);
  const ticks = Array.from({ length: TICKS + 1 }, (_, i) => (top / TICKS) * i);
  // x labels thin out rather than overlap: ~64px apart is comfortable for 11px
  // type, and the last label is always kept — it is the point the reader wants.
  const labelStep = Math.max(1, Math.ceil(count / Math.max(1, Math.floor(plotW / 64))));
  // A lone series gets the area wash under it, a marker on every point, and its
  // last value written on the line. None of the three survives contact with
  // several series: translucent fills overlap into a colour nobody's series is,
  // sixty markers are ink without information, and end labels stack into noise
  // wherever the lines converge. Then the legend and the tooltip carry it.
  const solo = series.length === 1;

  // Where the tooltip sits, and how it anchors. Near either edge it pulls itself
  // inside the plot rather than hanging off the card — which matters most at the
  // last point, since that is where keyboard focus lands.
  const tipX = active == null ? 0 : x(active);
  const tipAnchor =
    tipX > width - 130
      ? 'translateX(-100%)'
      : tipX < PAD.left + 130
        ? 'translateX(0)'
        : 'translateX(-50%)';

  const focusIndex = (i) => setActive(Math.min(count - 1, Math.max(0, i)));
  const onKeyDown = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    // The chart owns the arrow keys only while it has focus, so this does not
    // steal them from the page.
    e.preventDefault();
    focusIndex((active == null ? count - 1 : active) + (e.key === 'ArrowLeft' ? -1 : 1));
  };

  return (
    <div className="chart" ref={box}>
      <svg
        className="chart__svg"
        width={width}
        height={height}
        // 1:1 with the measured width, so nothing scales in the normal case. It is
        // here for the one case that does: a container narrower than the 320px
        // floor, where the viewBox shrinks the drawing instead of cropping its
        // right edge.
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={ariaLabel}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onFocus={() => setActive((a) => (a == null ? count - 1 : a))}
        onBlur={() => setActive(null)}
        onPointerLeave={() => setActive(null)}
      >
        <g className="chart__grid">
          {ticks.map((t) => (
            <line key={t} x1={PAD.left} x2={PAD.left + plotW} y1={y(t)} y2={y(t)} />
          ))}
        </g>
        <g className="chart__axis">
          {ticks.map((t) => (
            <text key={t} x={PAD.left - 10} y={y(t)} textAnchor="end" dominantBaseline="middle">
              {tick(t)}
            </text>
          ))}
          {labels.map((label, i) =>
            i % labelStep === 0 || i === count - 1 ? (
              <text key={label} x={x(i)} y={height - 8} textAnchor="middle">
                {formatLabel(label, i)}
              </text>
            ) : null
          )}
        </g>

        {/* The crosshair finds the x: the reader aims at a month, never at a 2px
            line. */}
        {active != null ? (
          <line
            className="chart__crosshair"
            x1={tipX}
            x2={tipX}
            y1={PAD.top}
            y2={PAD.top + plotH}
          />
        ) : null}

        {series.map((s) => {
          const color = colors.get(s.key);
          const values = s.values || [];
          const runs = runsOf(values);
          const last = runs.flat().pop();
          // Markers: every point for a lone series; for several, the ends and
          // whatever the pointer is on, which is what the reader is reading.
          const marks = solo
            ? runs.flat()
            : runs.flat().filter((i) => i === last || i === active);
          return (
            <g key={s.key}>
              {/* The fill is a wash of the line's own hue, never a saturated
                  block. */}
              {solo
                ? runs
                    .filter((run) => run.length > 1)
                    .map((run) => (
                      <path
                        key={`fill-${run[0]}`}
                        fill={color}
                        fillOpacity="0.1"
                        stroke="none"
                        d={`M${x(run[0])},${PAD.top + plotH} ${run
                          .map((i) => `L${x(i)},${y(values[i])}`)
                          .join(' ')} L${x(run[run.length - 1])},${PAD.top + plotH} Z`}
                      />
                    ))
                : null}
              {runs.map((run) => (
                <path
                  key={`line-${run[0]}`}
                  className="chart__line"
                  stroke={color}
                  d={run.map((i, n) => `${n === 0 ? 'M' : 'L'}${x(i)},${y(values[i])}`).join(' ')}
                />
              ))}
              {marks.map((i) => (
                <circle
                  key={`dot-${i}`}
                  className="chart__dot"
                  cx={x(i)}
                  cy={y(values[i])}
                  r={active === i ? 5.5 : 4}
                  fill={color}
                />
              ))}
              {solo && last != null ? (
                <text
                  className="chart__endlabel"
                  x={x(last) + (last === count - 1 && count > 1 ? -8 : 8)}
                  // Above the point, but never above the SVG: a series peaking at
                  // the axis top would have its label cropped by the frame.
                  y={Math.max(y(values[last]) - 12, 12)}
                  textAnchor={last === count - 1 && count > 1 ? 'end' : 'start'}
                >
                  {formatValue(values[last])}
                </text>
              ) : null}
            </g>
          );
        })}

        {/* Hit targets: one band per x, so the pointer only has to be nearest —
            never dead-centre on an 8px dot. */}
        <g className="chart__hits">
          {labels.map((label, i) => (
            <rect
              key={label}
              x={x(i) - band / 2}
              y={PAD.top}
              width={band}
              height={plotH}
              onPointerEnter={() => focusIndex(i)}
              onPointerMove={() => focusIndex(i)}
            />
          ))}
        </g>
      </svg>

      {active != null ? (
        <div className="chart__tip" role="status" style={{ left: tipX, transform: tipAnchor }}>
          <div className="chart__tip-when">{point(labels[active], active)}</div>
          {/* Every series at that x, so the pointer never has to land on a line to
              get a value — and the value leads each row, because the reader has the
              series and came for the number. */}
          {series.map((s) => {
            const v = (s.values || [])[active];
            return (
              <div key={s.key} className="chart__tip-row">
                <span className="chart__key" style={{ background: colors.get(s.key) }} />
                <strong className="chart__tip-value">
                  {v == null ? 'no data' : formatValue(v)}
                </strong>
                {solo ? null : <span className="chart__tip-label">{s.label ?? s.key}</span>}
              </div>
            );
          })}
        </div>
      ) : null}

      {/* A lone series is named by the card's own title, so a one-swatch legend
          would only restate it. Past that the legend is always present: identity is
          never left to colour-matching alone. */}
      {solo ? null : (
        <ul className="chart__legend">
          {series.map((s) => (
            <li key={s.key} className="chart__legend-item">
              <span className="chart__key" style={{ background: colors.get(s.key) }} />
              {s.label ?? s.key}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
